import BasicRecord from "./BasicRecord";
import { pluralize } from "./string";

const MS_IN_MINUTES = 60 * 1000;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
const MINUTES_IN_MONTH = MINUTES_IN_DAY * 30;
const MONTHS_IN_YEAR = 12;

type MaybeDate = Date | string;

enum DateEnum {
    Today = "today",
    Yesterday = "yesterday",
    ThisWeek = "this week",
    LastWeek = "last week",
    ThisMonth = "this month",
    LastMonth = "last month"
}

const DateLabels: Record<DateEnum, string> = {
    [DateEnum.Today]: "Today",
    [DateEnum.Yesterday]: "Yesterday",
    [DateEnum.ThisWeek]: "This Week",
    [DateEnum.LastWeek]: "Last Week",
    [DateEnum.ThisMonth]: "This Month",
    [DateEnum.LastMonth]: "Last Month"
};

function intoDate(maybeDate: MaybeDate): Date {
    return maybeDate instanceof Date ? maybeDate : new Date(maybeDate);
}

function getDateString(maybeDate: MaybeDate): string {
    const date = intoDate(maybeDate);

    const today = new Date();

    if (today.getMonth() === date.getMonth() && today.getFullYear() === date.getFullYear()) {
        if (today.getDate() === date.getDate()) {
            return DateEnum.Today;
        }

        if (today.getDate() - date.getDate() === 1) {
            return DateEnum.Yesterday;
        }
    }

    return formatTime(date, {
        year: today.getFullYear() !== date.getFullYear() ? "numeric" : undefined,
        month: "long",
        day: "numeric"
    });
}

function inSameDay(maybeDate1: MaybeDate, maybeDate2: MaybeDate): boolean {
    const date1 = intoDate(maybeDate1);
    const date2 = intoDate(maybeDate2);

    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
}

/**
 * Returns the difference between two dates in the given datePart format
 * @param {("w"|"d"|"h"|"m"|"s")} datePart
 * @param {Date} fromDate
 * @param {Date} toDate
 * @returns {Number} difference in the given datePart format
 */
function getDateDifference(datePart: string, fromDate: MaybeDate, toDate: MaybeDate): number {
    const divideBy: BasicRecord<number> = {
        y: 31536000000,
        w: 604800000,
        d: 86400000,
        h: 3600000,
        m: 60000,
        s: 1000
    };

    if (!datePart || !divideBy[datePart.toLowerCase()]) {
        throw new TypeError(`Unknown datePart type: '${datePart}'. datePart should be one of (y, w, d, h, m, s)`);
    }

    return Math.floor((intoDate(toDate).getTime() - intoDate(fromDate).getTime()) / divideBy[datePart.toLowerCase()]);
}

/**
 * Turns minutes value into a human readable time interval. Example, input is 120, output it 2 hours.
 * @param {number} minutes
 * @returns {string} human readable interval
 */
function turnMinutesIntoHumanReadableInterval(minutes: number): string {
    let amount;
    let unit;

    if (minutes < MINUTES_IN_HOUR) {
        amount = minutes;
        unit = "minute";
    } else if (minutes < MINUTES_IN_DAY) {
        amount = Math.floor(minutes / MINUTES_IN_HOUR);
        unit = "hour";
    } else if (minutes < MINUTES_IN_WEEK) {
        amount = Math.floor(minutes / MINUTES_IN_DAY);
        unit = "day";
    } else if (minutes < MINUTES_IN_MONTH) {
        amount = Math.floor(minutes / MINUTES_IN_WEEK);
        unit = "week";
    } else {
        amount = Math.floor(minutes / MINUTES_IN_MONTH);
        unit = "month";
    }

    return pluralize(amount, unit);
}

function getCalendarDateOfISOString(date: Date = new Date()): string {
    const ISO_STRING_CALENDAR_DATE_END_INDEX = 10;

    return date.toISOString().substr(0, ISO_STRING_CALENDAR_DATE_END_INDEX);
}

function getWeekday(date: Date): number {
    return (date.getDay() + 6) % 7;
}

function generateTimeSlots(): [label: DateEnum, date: Date][] {
    const oneday = 86400000; // 1000 * 60 * 60 * 24
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayTs = today.getTime();
    const yesterday = new Date(todayTs - oneday);
    const thisWeek = new Date(todayTs - getWeekday(now) * oneday);
    const lastWeek = new Date(todayTs - (getWeekday(now) + 7) * oneday);
    const thisMonth = new Date(todayTs - (now.getDate() - 1) * oneday);
    const lastMonth = new Date(todayTs - (now.getDate() + 1) * oneday);

    lastMonth.setDate(1);

    return [
        [DateEnum.Today, today],
        [DateEnum.Yesterday, yesterday],
        [DateEnum.ThisWeek, thisWeek],
        [DateEnum.LastWeek, lastWeek],
        [DateEnum.ThisMonth, thisMonth],
        [DateEnum.LastMonth, lastMonth]
    ];
}

function getTimeSlot(date: Date): string {
    const timeslot = generateTimeSlots().find(t => date >= t[1]);

    if (timeslot) {
        return timeslot[0];
    }

    const now = new Date();
    const formatOptions: Intl.DateTimeFormatOptions = { month: "long" };

    if (now.getFullYear() !== date.getFullYear()) {
        formatOptions.year = "numeric";
    }

    return formatTime(date, formatOptions);
}

function getTimeSlotLabel(date: Date): string {
    const timeslot = generateTimeSlots().find(t => date >= t[1]);

    if (timeslot) {
        return DateLabels[timeslot[0]];
    }

    const now = new Date();
    const formatOptions: Intl.DateTimeFormatOptions = { month: "long" };

    if (now.getFullYear() !== date.getFullYear()) {
        formatOptions.year = "numeric";
    }

    return formatTime(date, formatOptions);
}

function getMonthDifference(refDate: Date, baseDate: Date): number {
    return (baseDate.getMonth() - refDate.getMonth()) +
        (baseDate.getFullYear() - refDate.getFullYear()) * MONTHS_IN_YEAR;
}

function getDaysInTargetMonth(date: Date, targetMonth = 1): number {
    return new Date(date.getFullYear(), date.getMonth() + targetMonth, 0).getDate();
}

function getDaysBetweenSpecifiedMonths(refDate: Date, monthsLater: number): number {
    let totalDaysBetweenMonths = 0;
    const isLastDayOfTheMonth = getDaysInTargetMonth(refDate) === refDate.getDate();

    for (let i = 1; i <= monthsLater; i += 1) {
        // if reference date is the last day of the given month, then we must count days of the next month.
        const targetMonth = isLastDayOfTheMonth ? i + 1 : i;

        totalDaysBetweenMonths += getDaysInTargetMonth(refDate, targetMonth);
    }

    return totalDaysBetweenMonths;
}

function getMonthAgo(refDate: Date, baseDate: Date, actualDayDifference: number): number {
    const monthDifference = getMonthDifference(refDate, baseDate);
    const numOfDaysBetweenGivenMonths = getDaysBetweenSpecifiedMonths(refDate, monthDifference);

    // There are different days between each given months. (For instance while february and march has 28 or 29 days diff, march and april has 31 days diff.)
    // If actual day difference between given dates is less than their corresponding months' day difference, then calculated month ago should be decreased by one.
    if (numOfDaysBetweenGivenMonths > actualDayDifference) {
        return monthDifference - 1;
    }

    return monthDifference;
}

function microTimeAgo(refDateStr: string, baseDateStr?: string): string {
    const baseDate = baseDateStr ? new Date(Date.parse(baseDateStr)) : new Date();
    const refDate = new Date(Date.parse(refDateStr));
    const ms = baseDate.getTime() - refDate.getTime();
    const min = Math.floor(ms / MS_IN_MINUTES);
    const hr = Math.floor(min / MINUTES_IN_HOUR);
    const day = Math.floor(min / MINUTES_IN_DAY);
    const week = Math.floor(min / MINUTES_IN_WEEK);

    if (min < 1) {
        return "now";
    } else if (min < 60) {
        return `${min}m`;
    } else if (hr < 24) {
        return `${hr}h`;
    } else if (day < 7) {
        return `${day}d`;
    } else if (day < 28) {
        return `${week}w`;
    }

    const monthAgo = getMonthAgo(refDate, baseDate, day);

    if (monthAgo < 1) {
        return "4w";
    } else if (monthAgo < MONTHS_IN_YEAR) {
        return `${monthAgo}mth`;
    }

    return `${Math.floor(monthAgo / MONTHS_IN_YEAR)}y`;
}

function getOrdinalDateString(date: Date): string {
    function nth(number: number): string {
        let suffix: string;
        if (number > 3 && number < 21) {
            suffix = "th";
        } else {
            switch (number % 10) {
                case 1:
                    suffix = "st";
                    break;
                case 2:
                    suffix = "nd";
                    break;
                case 3:
                    suffix = "rd";
                    break;
                default:
                    suffix = "th";
                    break;
            }
        }

        return `${number}${suffix}`;
    }

    const monthString = date.toLocaleString("en-US", { month: "long" });

    return `${nth(date.getDate())} of ${monthString}`;
}

function formatDurationToMMSS(durationInSeconds: number): string {
    const secondsInMinute = 60;
    const maxLength = 2;

    const minutes = Math.floor(durationInSeconds / secondsInMinute);
    const seconds = Math.floor(durationInSeconds % secondsInMinute);

    return `${minutes.toString().padStart(maxLength, "0")}:${seconds.toString().padStart(maxLength, "0")}`;
}

const dateFormatterCache: Record<string, Intl.DateTimeFormat> = {};

function getDateFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const key = JSON.stringify(options);

    if (!dateFormatterCache[key]) {
        dateFormatterCache[key] = new Intl.DateTimeFormat("en", options);
    }

    return dateFormatterCache[key];
}

/**
 * Format given date according to the provided options.
 * Fallbacks to UTC timezone if runtime's default cannot be detected.
 * */
function formatTime(date: string | number | Date, options: Intl.DateTimeFormatOptions): string {
    let formatter = getDateFormatter(options);
    const { timeZone } = formatter.resolvedOptions();

    if (!timeZone || timeZone.toLowerCase() === "etc/unknown") {
        options.timeZone = "UTC";
        formatter = getDateFormatter(options);
    }

    return formatter.format(new Date(date));
}

function formatTimeWithGenericOptions(date: string | number | Date): string {
    return formatTime(date, {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC"
    });
}

export {
    getDateString,
    inSameDay,
    getDateDifference,
    turnMinutesIntoHumanReadableInterval,
    getCalendarDateOfISOString,
    getWeekday,
    generateTimeSlots,
    getTimeSlot,
    microTimeAgo,
    getTimeSlotLabel,
    getOrdinalDateString,
    formatDurationToMMSS,
    formatTime,
    formatTimeWithGenericOptions
};
